/**
 * Client-side validation for pipeline_config.apprise_vm (contract v2) — mirrors the
 * backend rules so problems are shown inline before submission. The backend
 * re-validates everything (and the pipeline preflight re-checks against Azure).
 *
 * Error / warning keys are dotted field paths: "server.server_name",
 * "network.public_ip.name", "storage.data_disks.1.size_gb",
 * "server.admin_source_cidrs.2" (per list item) …
 *
 * This file covers the always-required fields, the Server (Basics) and Network rules;
 * storage / security / operations live in appriseValidationOps.ts.
 */
import type { AppriseVmConfig, AppriseVmOptions, VmSizing } from '@/types'
import { cidrContains, isAlignedCidr, parseCidr, parseIpv4, type ParsedCidr } from './cidr'
import { AZURE_NAME_RE, VM_SIZE_RE, isValidResourceGroupName } from './azureNames'
import { PRODUCTION_ENVS } from './azureNames'
import { appriseRegionSupportsZones, newVirtualNetworkAllowed } from './appriseDefaults'
import { checkAdminSourceCidr, checkInboundSource, parsePortRange } from './appriseCidr'
import { sameImage } from './appriseCatalog'
import { validateOperations, validateSecurity, validateStorage } from './appriseValidationOps'

export interface AppriseValidationResult {
  errors: Record<string, string>
  warnings: Record<string, string>
}

export interface AppriseValidationContext {
  options: AppriseVmOptions
  region: string
  /** Portal environment (drives the stronger production warnings). */
  environment: string
  catalog: VmSizing
}

export type Errors = Record<string, string>

export const SERVER_NAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9-]{0,62}[a-zA-Z0-9]$/
/** A Windows computer name (NetBIOS) is at most 15 characters, so a new Windows VM's name is held to it. */
export const WINDOWS_NAME_MAX = 15
export const WINDOWS_NAME_MESSAGE =
  `A Windows VM name can have at most ${WINDOWS_NAME_MAX} characters: it is also the computer name, and the two must match`
export const WINDOWS_HOSTNAME_RE = /^[A-Za-z0-9-]{1,15}$/
export const LINUX_HOSTNAME_RE = /^[a-z0-9][a-z0-9-]{0,62}$/
export const ADMIN_USERNAME_RE = /^[a-zA-Z][a-zA-Z0-9_-]{0,19}$/
/** Reserved admin user names (Azure rejects these for Windows and Linux VMs). */
export const RESERVED_ADMIN_USERNAMES: ReadonlySet<string> = new Set([
  'administrator', 'admin', 'admin1', 'admin2', 'root', 'user', 'user1', 'user2', 'user3', 'user4', 'user5', 'guest', 'sa',
  'test', 'test1', 'test2', 'test3', 'owner', 'server', 'sql', 'support', 'sys', 'backup', 'console', 'adm', 'aspnet',
  'actuser', 'david', 'john', 'a', 'daemon', 'bin', 'sshd', 'nobody', 'operator', 'www-data', 'sudo', 'shadow', 'staff',
])
export const GUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
export const DNS_LABEL_RE = /^[a-z][a-z0-9-]{1,61}[a-z0-9]$/
export const TIME_ZONE_RE = /^[A-Za-z0-9 .,()_+-]{1,100}$/
export const IMAGE_PART_RE = /^[A-Za-z0-9._-]{1,128}$/
export const IMAGE_VERSION_RE = /^(latest|\d+\.\d+\.\d+)$/
export const AVAILABILITY_SET_ID_RE =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Compute\/availabilitySets\/[^/]+$/i
export const VMSS_ID_RE =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Compute\/virtualMachineScaleSets\/[^/]+$/i
export const LB_BACKEND_POOL_ID_RE =
  /^\/subscriptions\/[^/]+\/resourceGroups\/[^/]+\/providers\/Microsoft\.Network\/loadBalancers\/[^/]+\/backendAddressPools\/[^/]+$/i
export const NSG_RULE_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,79}$/

const AVAILABILITY_TYPES = new Set(['none', 'zone', 'availability_set', 'vmss'])
const NAME_RULE = '2–80 letters, digits, _ . -'

export const nonBlank = (items: string[]) => items.map((s) => s.trim()).filter(Boolean)

export function expectedHostnameRule(os: 'windows' | 'linux'): { re: RegExp; message: string } {
  return os === 'windows'
    ? { re: WINDOWS_HOSTNAME_RE, message: '1–15 letters, digits or hyphens (Windows computer name), not all digits' }
    : { re: LINUX_HOSTNAME_RE, message: 'Lowercase letters, digits and hyphens; must start with a letter or digit (max 63)' }
}

function checkNetworkCidr(value: string, label: string): { cidr: ParsedCidr | null; error?: string } {
  if (!value.trim()) return { cidr: null, error: `${label} is required` }
  const cidr = parseCidr(value)
  if (!cidr) return { cidr: null, error: `${label} must be a valid IPv4 CIDR (e.g. 172.16.0.0/16)` }
  if (!isAlignedCidr(cidr)) return { cidr: null, error: `${label} has host bits set — use the network address of the block` }
  return { cidr }
}

function validateAdminAccess(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors) {
  const s = c.server
  if (!ctx.options.admin_access_methods.includes(s.admin_access_method)) errors['server.admin_access_method'] = 'Choose an admin access method'
  if (s.admin_access_method === 'private_network') {
    const cidrs = nonBlank(s.admin_source_cidrs)
    if (cidrs.length === 0) {
      errors['server.admin_source_cidrs'] = 'At least one admin source range is required for private network access'
    }
    s.admin_source_cidrs.forEach((raw, i) => {
      if (!raw.trim()) return
      const res = checkAdminSourceCidr(raw)
      if (res.error) errors[`server.admin_source_cidrs.${i}`] = res.error
    })
  }
}

export function validateAppriseVm(config: AppriseVmConfig, ctx: AppriseValidationContext): AppriseValidationResult {
  const errors: Errors = {}
  const warnings: Errors = {}
  const s = config.server

  // ── Always required (also for an existing server) ──
  if (!s.server_name.trim()) errors['server.server_name'] = 'Virtual machine name is required'
  else if (!SERVER_NAME_RE.test(s.server_name))
    errors['server.server_name'] = '2–64 letters, digits or hyphens; must start and end with a letter or digit'

  if (!s.resource_group_name.trim()) errors['server.resource_group_name'] = 'Resource group name is required'
  else if (!isValidResourceGroupName(s.resource_group_name))
    errors['server.resource_group_name'] = 'Up to 90 letters, digits, - _ . ( ) and must not end with a period'

  if (s.os_type !== 'windows' && s.os_type !== 'linux') errors['server.os_type'] = 'Choose the operating system'

  validateAdminAccess(config, ctx, errors)

  // Existing server: no Terraform runs, only a control-plane verification.
  if (s.deployment_mode === 'use_existing') return { errors, warnings }

  validateServer(config, ctx, errors, warnings)
  validateNetwork(config, ctx, errors, warnings)
  validateStorage(config, ctx, errors, warnings)
  validateSecurity(config, ctx, errors)
  validateOperations(config, ctx, errors)
  return { errors, warnings }
}

function validateAvailability(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors) {
  const a = c.server.availability
  if (!AVAILABILITY_TYPES.has(a.type)) {
    errors['server.availability.type'] = 'Choose an availability option'
    return
  }
  if (a.type === 'zone') {
    if (!['1', '2', '3'].includes(a.zone)) errors['server.availability.zone'] = 'Choose a zone (1, 2 or 3)'
    else if (!appriseRegionSupportsZones(ctx.region, ctx.options)) errors['server.availability.zone'] = `${ctx.region} does not support availability zones`
  } else if (a.zone) errors['server.availability.zone'] = 'A zone can only be set with the “Availability zone” option'

  if (a.type === 'availability_set') {
    if (!AVAILABILITY_SET_ID_RE.test(a.availability_set_id.trim())) errors['server.availability.availability_set_id'] = 'Choose an existing availability set'
  } else if (a.availability_set_id.trim()) errors['server.availability.availability_set_id'] = 'An availability set can only be set with the “Availability set” option'

  if (a.type === 'vmss') {
    if (!VMSS_ID_RE.test(a.vmss_id.trim())) errors['server.availability.vmss_id'] = 'Choose an existing virtual machine scale set'
  } else if (a.vmss_id.trim()) errors['server.availability.vmss_id'] = 'A scale set can only be set with the “Virtual machine scale set” option'
}

function validateServer(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors, warnings: Errors) {
  const s = c.server
  const os = s.os_type

  // A Windows computer name is limited to 15 characters; the VM name is held to the same limit so the two are identical.
  if (os === 'windows' && s.server_name.trim().length > WINDOWS_NAME_MAX && !errors['server.server_name'])
    errors['server.server_name'] = WINDOWS_NAME_MESSAGE

  if (os === 'windows' || os === 'linux') {
    const rule = expectedHostnameRule(os)
    if (!s.hostname.trim()) errors['server.hostname'] = 'Hostname is required'
    else if (!rule.re.test(s.hostname) || (os === 'windows' && /^\d+$/.test(s.hostname)))
      errors['server.hostname'] = `The computer name is derived from the VM name: ${rule.message}`

    if (os === 'linux' && s.time_zone.trim()) errors['server.time_zone'] = 'Time zone applies to Windows servers only'
    if (os === 'windows' && s.time_zone.trim() && !TIME_ZONE_RE.test(s.time_zone.trim()))
      errors['server.time_zone'] = 'Use a Windows time zone ID, e.g. UTC or Eastern Standard Time'
  }

  const img = s.image
  if (!IMAGE_PART_RE.test(img.publisher) || !IMAGE_PART_RE.test(img.offer) || !IMAGE_PART_RE.test(img.sku) || !IMAGE_VERSION_RE.test(img.version))
    errors['server.image'] = 'Choose an image (publisher, offer and SKU: letters, digits, . _ -; version "latest" or x.y.z)'
  else if (!ctx.options.images.some((i) => i.verified_for_product && sameImage(i, img)))
    warnings['server.image'] = 'This image is not verified for Apprise'

  if (!VM_SIZE_RE.test(s.vm_size)) errors['server.vm_size'] = 'Choose an Azure VM size, e.g. Standard_D2s_v3'
  validateAvailability(c, ctx, errors)
  if (!ctx.options.server_environments.includes(s.server_environment)) errors['server.server_environment'] = 'Choose an environment'

  if (!ADMIN_USERNAME_RE.test(s.admin_username))
    errors['server.admin_username'] = '1–20 characters; start with a letter, then letters, digits, _ or -'
  else if (RESERVED_ADMIN_USERNAMES.has(s.admin_username.toLowerCase()))
    errors['server.admin_username'] = `"${s.admin_username}" is a reserved name — choose another`

  s.rbac_admin_principal_ids.forEach((raw, i) => {
    if (raw.trim() && !GUID_RE.test(raw.trim())) errors[`server.rbac_admin_principal_ids.${i}`] = 'Must be an Entra ID object ID (GUID)'
  })

  if (s.license_type !== 'windows_server' && s.license_type !== 'none') errors['server.license_type'] = 'Choose a licensing option'
  else if (os === 'windows' && s.license_type === 'windows_server' && !s.license_confirmed)
    errors['server.license_confirmed'] = 'Confirm that you have an eligible Windows Server license (Azure Hybrid Benefit), or turn the benefit off'
  else if (os === 'linux' && (s.license_type !== 'none' || s.license_confirmed))
    errors['server.license_type'] = 'Azure Hybrid Benefit for Windows Server does not apply to Linux images'

  const cat = ctx.catalog
  if (!cat.overrides_allowed && s.vm_size !== cat.vm_size) errors['server.vm_size'] = 'This SKU does not allow VM size overrides'
}

function validateNetwork(c: AppriseVmConfig, ctx: AppriseValidationContext, errors: Errors, warnings: Errors) {
  const n = c.network
  let vnet: ParsedCidr | null = null
  let subnet: ParsedCidr | null = null

  if (n.vnet_mode === 'create_new' && !newVirtualNetworkAllowed(ctx.options))
    errors['network.vnet_mode'] = 'New virtual networks are not created from the portal: choose an existing virtual network'
  if (!n.vnet_name.trim()) errors['network.vnet_name'] = n.vnet_mode === 'existing' ? 'Choose a virtual network' : 'Virtual network name is required'
  else if (!AZURE_NAME_RE.test(n.vnet_name)) errors['network.vnet_name'] = NAME_RULE
  if (n.vnet_mode === 'create_new') {
    const res = checkNetworkCidr(n.vnet_address_space, 'Address space')
    vnet = res.cidr
    if (res.error) errors['network.vnet_address_space'] = res.error
  } else if (!n.vnet_resource_group.trim()) errors['network.vnet_resource_group'] = 'Resource group of the existing virtual network is required'
  else if (!isValidResourceGroupName(n.vnet_resource_group)) errors['network.vnet_resource_group'] = 'Invalid resource group name'

  if (!n.subnet_name.trim()) errors['network.subnet_name'] = n.subnet_mode === 'existing' ? 'Choose a subnet' : 'Subnet name is required'
  else if (!AZURE_NAME_RE.test(n.subnet_name)) errors['network.subnet_name'] = NAME_RULE
  if (n.subnet_mode === 'create_new') {
    const res = checkNetworkCidr(n.subnet_prefix, 'Subnet prefix')
    subnet = res.cidr
    if (res.error) errors['network.subnet_prefix'] = res.error
    else if (vnet && subnet && !cidrContains(vnet, subnet))
      errors['network.subnet_prefix'] = `Subnet must be inside the virtual network address space ${n.vnet_address_space}`
  }
  if (n.vnet_mode === 'create_new' && n.subnet_mode === 'existing')
    errors['network.subnet_mode'] = 'A new virtual network has no existing subnets — create a new subnet'

  if (n.private_ip_allocation === 'static') {
    const ip = parseIpv4(n.private_ip_address)
    if (ip === null) errors['network.private_ip_address'] = 'Must be a valid IPv4 address'
    else if (subnet) {
      if (ip < subnet.start || ip > subnet.end) errors['network.private_ip_address'] = `Must be inside the subnet ${n.subnet_prefix}`
      else if (ip < subnet.start + 4 || ip === subnet.end) errors['network.private_ip_address'] = 'Azure reserves the first four and the last address of a subnet'
    }
  }

  const pip = n.public_ip
  if (!['create_new', 'existing', 'none'].includes(pip.mode)) errors['network.public_ip.mode'] = 'Choose a public IP option'
  if (pip.mode === 'create_new') {
    if (!pip.name.trim()) errors['network.public_ip.name'] = 'Public IP name is required'
    else if (!AZURE_NAME_RE.test(pip.name)) errors['network.public_ip.name'] = NAME_RULE
  }
  if (pip.mode === 'existing') {
    if (!pip.name.trim()) errors['network.public_ip.name'] = 'Choose an existing public IP'
    if (!pip.resource_group.trim()) errors['network.public_ip.resource_group'] = 'Resource group of the existing public IP is required'
    else if (!isValidResourceGroupName(pip.resource_group)) errors['network.public_ip.resource_group'] = 'Invalid resource group name'
  }
  if (pip.mode !== 'none' && (PRODUCTION_ENVS.has(ctx.environment) || c.server.server_environment === 'production')) {
    warnings['network.public_ip.mode'] =
      'Production server: a public IP exposes it directly to the internet. Strongly discouraged — prefer a private network, Azure Bastion or JIT.'
  }

  if (!['create_new', 'none', 'existing'].includes(n.nsg_mode)) errors['network.nsg_mode'] = 'Choose a network security group option'
  if (n.nsg_mode === 'create_new') {
    if (!n.nsg_name.trim()) errors['network.nsg_name'] = 'NSG name is required'
    else if (!AZURE_NAME_RE.test(n.nsg_name)) errors['network.nsg_name'] = NAME_RULE
  } else if (n.nsg_mode === 'existing') {
    if (!n.nsg_name.trim()) errors['network.nsg_name'] = 'Choose an existing network security group'
    if (!n.nsg_resource_group.trim()) errors['network.nsg_resource_group'] = 'Resource group of the existing NSG is required'
    else if (!isValidResourceGroupName(n.nsg_resource_group)) errors['network.nsg_resource_group'] = 'Invalid resource group name'
  }

  validateInboundRules(c, errors)

  if (!ctx.options.outbound_modes.includes(n.outbound_mode)) errors['network.outbound_mode'] = 'Choose an outbound mode'
  else if (n.outbound_mode === 'restricted' && n.nsg_mode === 'existing')
    errors['network.outbound_mode'] = 'Restricted outbound adds a deny-Internet rule, so it needs a newly created NSG (a shared NSG is never modified)'
  n.dns_servers.forEach((raw, i) => {
    if (raw.trim() && parseIpv4(raw) === null) errors[`network.dns_servers.${i}`] = 'Must be a valid IPv4 address'
  })
  if (pip.mode === 'create_new' && n.dns_label.trim() && !DNS_LABEL_RE.test(n.dns_label.trim()))
    errors['network.dns_label'] = '3–63 lowercase letters, digits or hyphens; start with a letter'

  const lb = n.load_balancer
  if (lb.mode !== 'none' && lb.mode !== 'existing') errors['network.load_balancer.mode'] = 'Choose a load balancing option'
  else if (lb.mode === 'existing' && !LB_BACKEND_POOL_ID_RE.test(lb.backend_pool_id.trim()))
    errors['network.load_balancer.backend_pool_id'] = 'Choose a load balancer and one of its backend pools'
}

function validateInboundRules(c: AppriseVmConfig, errors: Errors) {
  const rules = c.network.inbound_rules
  const seen = new Map<number, number>()
  rules.forEach((r, i) => {
    const key = (f: string) => `network.inbound_rules.${i}.${f}`
    if (!NSG_RULE_NAME_RE.test(r.name)) errors[key('name')] = 'Letters, digits, _ . - (max 80); must start with a letter or digit'
    if (!Number.isInteger(r.priority) || r.priority < 200 || r.priority > 4000) errors[key('priority')] = 'Whole number between 200 and 4000'
    else if (seen.has(r.priority)) errors[key('priority')] = `Priority already used by rule ${(seen.get(r.priority) as number) + 1}`
    else seen.set(r.priority, i)
    if (r.protocol !== 'Tcp' && r.protocol !== 'Udp') errors[key('protocol')] = 'Choose TCP or UDP'
    const port = parsePortRange(r.port)
    if (!port) errors[key('port')] = 'A port 1–65535 or a range such as 8000-8100'
    if (!r.source_cidr.trim()) errors[key('source_cidr')] = 'Source range is required'
    else {
      const res = checkInboundSource(r.source_cidr, port, c.network.public_ip.mode !== 'none')
      if (res.error) errors[key('source_cidr')] = res.error
    }
  })
}

export function hasAppriseErrors(result: AppriseValidationResult): boolean {
  return Object.keys(result.errors).length > 0
}
